// src/contexts/QuoteContext.tsx

import React, { createContext, useState, ReactNode } from "react";

interface Service {
  id: string;
  nameKey: string; // 用于i18n翻译的键
  unitPrice: number;
  unit: number;
  total: number;
  noteKey?: string; // 若有备注说明，也用i18n key表示
  maxUnit?: number;
}

interface Category {
  id: string;
  nameKey?: string; // 分类名称使用key
  services: Service[];
  descriptionKey?: string; // 描述文本的key
  isDescriptionOnly?: boolean;
}

interface QuoteContextProps {
  basic: Category[];
  investment: Category[];
  foreignIncome: Category[];
  otherMisc: Category[];
  setBasic: React.Dispatch<React.SetStateAction<Category[]>>;
  setInvestment: React.Dispatch<React.SetStateAction<Category[]>>;
  setForeignIncome: React.Dispatch<React.SetStateAction<Category[]>>;
  setOtherMisc: React.Dispatch<React.SetStateAction<Category[]>>;
  resetAll: () => void;
}

export const QuoteContext = createContext<QuoteContextProps | undefined>(
  undefined
);

export const QuoteProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [basic, setBasic] = useState<Category[]>([
    {
      id: "federal_basic",
      nameKey: "taxQuote.basicPackages.federalBasic",
      services: [
        {
          id: "single_federal",
          nameKey: "taxQuote.basicPackages.singleFederal",
          unitPrice: 135.0,
          unit: 0,
          total: 0,
        },
        {
          id: "married_federal",
          nameKey: "taxQuote.basicPackages.marriedFederal",
          unitPrice: 210.0,
          unit: 0,
          total: 0,
        },
        {
          id: "head_federal",
          nameKey: "taxQuote.basicPackages.headFederal",
          unitPrice: 175.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "complete_basic",
      nameKey: "taxQuote.basicPackages.completeBasic",
      services: [
        {
          id: "single_complete",
          nameKey: "taxQuote.basicPackages.singleComplete",
          unitPrice: 180.0,
          unit: 0,
          total: 0,
        },
        {
          id: "married_complete",
          nameKey: "taxQuote.basicPackages.marriedComplete",
          unitPrice: 255.0,
          unit: 0,
          total: 0,
        },
        {
          id: "head_complete",
          nameKey: "taxQuote.basicPackages.headComplete",
          unitPrice: 220.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "basic_description",
      services: [
        {
          id: "basic_description_note",
          nameKey: "taxQuote.basicPackages.basicDescriptionTitle", // 这里可以不使用nameKey，留作兼容
          unitPrice: 0,
          unit: 0,
          total: 0,
          noteKey: "taxQuote.basicPackages.basicDescriptionNote",
        },
      ],
      isDescriptionOnly: true,
      descriptionKey: "taxQuote.basicPackages.basicDescriptionTitle",
    },
    {
      id: "add_ons",
      nameKey: "taxQuote.basicPackages.addOns",
      services: [
        {
          id: "more_w2",
          nameKey: "taxQuote.basicPackages.moreW2",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "more_1099_int",
          nameKey: "taxQuote.basicPackages.more1099INT",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "more_1099_div",
          nameKey: "taxQuote.basicPackages.more1099DIV",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "additional_state",
          nameKey: "taxQuote.basicPackages.additionalState",
          unitPrice: 35.0,
          unit: 0,
          total: 0,
        },
      ],
    },
  ]);

  const [investment, setInvestment] = useState<Category[]>([
    {
      id: "stock_investment",
      nameKey: "taxQuote.investment.stockInvestment",
      services: [
        {
          id: "regular_1099b",
          nameKey: "taxQuote.investment.regular1099B",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
          noteKey: "taxQuote.investment.regular1099BNote",
        },
        {
          id: "special_1099b",
          nameKey: "taxQuote.investment.special1099B",
          unitPrice: 25.0,
          unit: 0,
          total: 0,
          noteKey: "taxQuote.investment.special1099BNote",
        },
        {
          id: "1099r",
          nameKey: "taxQuote.investment.1099R",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "1099sa",
          nameKey: "taxQuote.investment.1099SA",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "1099g",
          nameKey: "taxQuote.investment.1099G",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "business_investment",
      nameKey: "taxQuote.investment.businessInvestment",
      services: [
        {
          id: "schedule_k1",
          nameKey: "taxQuote.investment.scheduleK1",
          unitPrice: 40.0,
          unit: 0,
          total: 0,
        },
        {
          id: "1099_misc",
          nameKey: "taxQuote.investment.1099MISC",
          unitPrice: 10.0,
          unit: 0,
          total: 0,
        },
        {
          id: "1099_nec",
          nameKey: "taxQuote.investment.1099NEC",
          unitPrice: 30.0,
          unit: 0,
          total: 0,
        },
        {
          id: "self_employment_nec",
          nameKey: "taxQuote.investment.selfEmploymentNEC",
          unitPrice: 60.0,
          unit: 0,
          total: 0,
        },
        {
          id: "self_employment_schedule_c",
          nameKey: "taxQuote.investment.selfEmploymentScheduleC",
          unitPrice: 100.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "real_estate_investment",
      nameKey: "taxQuote.investment.realEstateInvestment",
      services: [
        {
          id: "rental_properties",
          nameKey: "taxQuote.investment.rentalProperties",
          unitPrice: 45.0,
          unit: 0,
          total: 0,
        },
        {
          id: "home_sale_primary",
          nameKey: "taxQuote.investment.homeSalePrimary",
          unitPrice: 80.0,
          unit: 0,
          total: 0,
        },
        {
          id: "home_sale_investment",
          nameKey: "taxQuote.investment.homeSaleInvestment",
          unitPrice: 120.0,
          unit: 0,
          total: 0,
        },
      ],
    },
  ]);

  const [foreignIncome, setForeignIncome] = useState<Category[]>([
    {
      id: "foreign_income",
      nameKey: "taxQuote.foreignIncomeAndAssets.foreignIncomeW2",
      services: [
        {
          id: "form_2555",
          nameKey: "taxQuote.foreignIncomeAndAssets.form2555",
          unitPrice: 75.0,
          unit: 0,
          total: 0,
        },
        {
          id: "tax_credit",
          nameKey: "taxQuote.foreignIncomeAndAssets.foreignTaxCredit",
          unitPrice: 75.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "foreign_gift",
      nameKey: "taxQuote.foreignIncomeAndAssets.foreignGift",
      services: [
        {
          id: "form_3520",
          nameKey: "taxQuote.foreignIncomeAndAssets.form3520",
          unitPrice: 30.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "foreign_assets",
      nameKey: "taxQuote.foreignIncomeAndAssets.foreignAssets",
      services: [
        {
          id: "fbar",
          nameKey: "taxQuote.foreignIncomeAndAssets.fbar",
          unitPrice: 60.0,
          unit: 0,
          total: 0,
        },
        {
          id: "fatca",
          nameKey: "taxQuote.foreignIncomeAndAssets.fatca",
          unitPrice: 60.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "foreign_ownership",
      nameKey: "taxQuote.foreignIncomeAndAssets.foreignOwnership",
      services: [
        {
          id: "form_5471",
          nameKey: "taxQuote.foreignIncomeAndAssets.form5471",
          unitPrice: 300.0,
          unit: 0,
          total: 0,
        },
      ],
    },
  ]);

  const [otherMisc, setOtherMisc] = useState<Category[]>([
    {
      id: "other_misc",
      nameKey: "taxQuote.otherMISC.otherMISC",
      services: [
        {
          id: "itin_application_form",
          nameKey: "taxQuote.otherMISC.itinAppForm",
          unitPrice: 250.0,
          unit: 0,
          total: 0,
        },
        {
          id: "tax_estimated",
          nameKey: "taxQuote.otherMISC.taxEstimated",
          unitPrice: 50.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "amended_federal",
      nameKey: "taxQuote.otherMISC.amendedFederal",
      services: [
        {
          id: "amended_federal_single",
          nameKey: "taxQuote.otherMISC.amendedFederalSingle",
          unitPrice: 120.0,
          unit: 0,
          total: 0,
        },
        {
          id: "amended_federal_married",
          nameKey: "taxQuote.otherMISC.amendedFederalMarried",
          unitPrice: 180.0,
          unit: 0,
          total: 0,
        },
        {
          id: "amended_federal_head",
          nameKey: "taxQuote.otherMISC.amendedFederalHead",
          unitPrice: 150.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "amended_federal_state",
      nameKey: "taxQuote.otherMISC.amendedFederalState",
      services: [
        {
          id: "amended_federal_state_single",
          nameKey: "taxQuote.otherMISC.amendedFederalStateSingle",
          unitPrice: 150.0,
          unit: 0,
          total: 0,
        },
        {
          id: "amended_federal_state_married",
          nameKey: "taxQuote.otherMISC.amendedFederalStateMarried",
          unitPrice: 220.0,
          unit: 0,
          total: 0,
        },
        {
          id: "amended_federal_state_head",
          nameKey: "taxQuote.otherMISC.amendedFederalStateHead",
          unitPrice: 180.0,
          unit: 0,
          total: 0,
        },
      ],
    },
    {
      id: "amended_federal_state_description",
      services: [
        {
          id: "amended_federal_state_description_note",
          nameKey: "taxQuote.otherMISC.amendedFederalStateTitle", // 这里可以不使用nameKey，留作兼容
          unitPrice: 0,
          unit: 0,
          total: 0,
          noteKey: "taxQuote.otherMISC.amendedFederalStateTitleNote",
        },
      ],
      isDescriptionOnly: true,
      descriptionKey: "taxQuote.otherMISC.amendedFederalStateTitle",
    },
  ]);

  const resetAll = () => {
    setBasic(
      basic.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          unit: 0,
          total: 0,
        })),
      }))
    );

    setInvestment(
      investment.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          unit: 0,
          total: 0,
        })),
      }))
    );

    setForeignIncome(
      foreignIncome.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          unit: 0,
          total: 0,
        })),
      }))
    );

    setOtherMisc(
      otherMisc.map((category) => ({
        ...category,
        services: category.services.map((service) => ({
          ...service,
          unit: 0,
          total: 0,
        })),
      }))
    );
  };

  return (
    <QuoteContext.Provider
      value={{
        basic,
        investment,
        foreignIncome,
        otherMisc,
        setBasic,
        setInvestment,
        setForeignIncome,
        setOtherMisc,
        resetAll,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};
